import React, { useEffect, useState, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Select,
  IconButton,
} from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import regionStore from "../../stores/RegionStore";
import CustomDataGridPro from "../CustomDataGridPro";

const RegionComponent = observer(() => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRegionId, setCurrentRegionId] = useState(null);
  const [regionData, setRegionData] = useState({
    name: "",
    description: "",
    managerId: "",
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    regionStore.fetchRegions();
    regionStore.fetchManagers();
  }, []);

  /** Stable function for opening the modal */
  const handleOpenModal = useCallback((region = null) => {
    if (region) {
      setRegionData(region);
      setEditMode(true);
      setCurrentRegionId(region.id);
    } else {
      setRegionData({ name: "", description: "", managerId: "" });
      setEditMode(false);
    }
    setIsModalOpen(true);
  }, []);

  /** Stable function for saving region */
  const handleSave = useCallback(async () => {
    if (editMode) {
      await regionStore.updateRegion(currentRegionId, regionData);
    } else {
      await regionStore.createRegion(regionData);
    }
    setIsModalOpen(false);
  }, [editMode, currentRegionId, regionData]);

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        flex: 0.6,
        minWidth: 100,
      },
      {
        field: "name",
        headerName: "Region Name",
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: "description",
        headerName: "Region Description",
        flex: 1.6,
        minWidth: 240,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 160, // keep fixed so buttons don’t stretch
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box>
            <IconButton
              onClick={() => handleOpenModal(params.row.raw)}
              sx={{
                color: "#22C55E",
                mr: 1,
                "&:hover": { backgroundColor: "#BBF7D0" },
              }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={() => regionStore.deleteRegion(params.row.id)}
              sx={{
                color: "#6B7280",
                "&:hover": { backgroundColor: "#E5E7EB" },
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ),
      },
    ],
    [handleOpenModal]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Card
        elevation={3}
        sx={{
          mb: 4,
          p: 2,
          background: "#F3F4F6",
          borderLeft: "4px solid #22C55E",
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={1}>
            <MapIcon fontSize="large" sx={{ mr: 2, color: "#22C55E" }} />
            <Typography variant="h4" fontWeight="bold">
              Regions Management
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage your region details from this dashboard
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            backgroundColor: "#22C55E",
            "&:hover": { backgroundColor: "#16A34A" },
          }}
          onClick={() => handleOpenModal()}
        >
          Add Region
        </Button>
      </Box>

      <CustomDataGridPro
        rows={regionStore.regions}
        columns={columns}
        isSkeletonOnly={regionStore.isLoading}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalCount={regionStore.regions.length}
      />

      {/* Modal */}
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <DialogTitle>{editMode ? "Edit Region" : "Add New Region"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Region Name"
            fullWidth
            margin="normal"
            value={regionData.name}
            onChange={(e) =>
              setRegionData((prev) => ({ ...prev, name: e.target.value }))
            }
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            value={regionData.description}
            onChange={(e) =>
              setRegionData((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
          />
          <Select
            fullWidth
            value={regionData.managerId}
            onChange={(e) =>
              setRegionData((prev) => ({ ...prev, managerId: e.target.value }))
            }
          >
            {regionStore.managers.map((manager) => (
              <MenuItem key={manager.id} value={manager.id}>
                {manager.name}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>{editMode ? "Update" : "Add"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default RegionComponent;
